import { useEffect, useState } from "react";
import { Check, X, Send, Sliders } from "lucide-react";
import { adminHttp, formatApiError } from "../../lib/adminApi";
import { toast } from "sonner";
import { useAuth } from "../AuthContext";

export default function EmailsSettings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTpl, setTestTpl] = useState("quote_received");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [delays, setDelays] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminHttp.get("/admin/emails/settings");
      setData(data);
      setDelays(data.delays || {});
      setTestEmail(user?.email || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (key, enabled) => {
    try {
      await adminHttp.post("/admin/emails/settings/toggle", { key, enabled });
      toast.success(`Plantilla ${enabled ? "activada" : "desactivada"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDelays = async () => {
    try {
      await adminHttp.post("/admin/emails/settings/delays", delays);
      toast.success("Delays actualizados");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const testSend = async () => {
    setSending(true);
    try {
      const res = await adminHttp.post("/admin/emails/test", {
        template_key: testTpl,
        to_email: testEmail,
      });
      const payload = res.data || {};
      if (payload.status === "sent") toast.success("Email de prueba enviado");
      else if (payload.status === "skipped") toast.message(`Omitido: ${payload.reason}`);
      else toast.error(`Error: ${payload.error || "no enviado"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (loading || !data) return <div className="p-10 text-[#6B7280] font-mono text-xs uppercase tracking-widest">Cargando…</div>;

  return (
    <div className="p-6 lg:p-10" data-testid="emails-settings-page">
      <div className="flex items-center gap-3 mb-2">
        <span className="divider-accent" /><span className="kicker">Configuración</span>
      </div>
      <h1 className="font-display font-black tracking-tightest text-4xl md:text-5xl">Ajustes de email</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        {/* Resend status */}
        <div className="bg-white border border-[#DDE2D5] p-6">
          <h2 className="font-display font-black text-xl mb-4">Estado Resend</h2>
          <div className="flex items-center gap-2 mb-4">
            <span className={`inline-block w-2 h-2 rounded-full ${data.resend.ok ? "bg-green-400" : "bg-red-400"}`} />
            <span className="font-mono text-xs text-[#374151]">{data.resend.ok ? "Conectado" : "Sin conexión"}</span>
          </div>
          <Row label="Remitente">{data.resend.from}</Row>
          <Row label="Reply-to">{(data.resend.reply_to || []).join(", ") || "—"}</Row>
          <Row label="Internos (alertas)">{(data.resend.internal || []).join(", ") || "—"}</Row>
          <Row label="Site URL">{data.resend.site_url || "—"}</Row>
        </div>

        {/* Test send */}
        <div className="bg-white border border-[#DDE2D5] p-6">
          <h2 className="font-display font-black text-xl mb-4 flex items-center gap-2"><Send className="w-4 h-4 text-accent" /> Enviar prueba</h2>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Plantilla</label>
          <select value={testTpl} onChange={(e) => setTestTpl(e.target.value)}
                  className="w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent mb-3"
                  data-testid="test-template">
            {(data.templates || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Destinatario</label>
          <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                 placeholder="tu@email.com"
                 className="w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm focus:outline-none focus:border-accent mb-4"
                 data-testid="test-email" />
          <button onClick={testSend} disabled={sending || !testEmail}
                  className="btn-accent w-full justify-center" data-testid="test-send-btn">
            {sending ? "Enviando…" : "Enviar email de prueba"}
          </button>
          <p className="mt-3 text-[11px] text-[#6B7280] leading-relaxed">
            En modo sandbox (onboarding@resend.dev), Resend solo permite enviar a la cuenta verificada.
            Una vez verificado el dominio velariastens.com.mx, cambia `RESEND_FROM_EMAIL` en el .env.
          </p>
        </div>
      </div>

      {/* Templates toggle */}
      <div className="mt-8 bg-white border border-[#DDE2D5]">
        <div className="px-6 py-4 border-b border-[#DDE2D5]">
          <h2 className="font-display font-black text-xl">Plantillas activas</h2>
        </div>
        <div className="divide-y divide-white/5">
          {(data.templates || []).map((t) => (
            <div key={t.key} className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="font-display font-bold text-[#1F2937]">{t.label}</p>
                <p className="font-mono text-[11px] text-[#6B7280] mt-1">{t.key}</p>
              </div>
              <button onClick={() => toggle(t.key, !t.enabled)}
                      disabled={user?.role !== "admin"}
                      className={`w-14 h-7 rounded-full flex items-center transition ${t.enabled ? "bg-accent" : "bg-white/10"} ${user?.role !== "admin" ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`toggle-${t.key}`}>
                <span className={`w-5 h-5 rounded-full bg-white transform transition ${t.enabled ? "translate-x-8" : "translate-x-1"}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Delays */}
      <div className="mt-8 bg-white border border-[#DDE2D5] p-6">
        <h2 className="font-display font-black text-xl mb-4 flex items-center gap-2"><Sliders className="w-4 h-4 text-accent" /> Tiempos de envío</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DelayInput label="Cotización preliminar (min)" k="preliminary_quote_minutes" delays={delays} setDelays={setDelays} />
          <DelayInput label="Nurturing día 1 (horas)" k="nurturing_day_1_hours" delays={delays} setDelays={setDelays} />
          <DelayInput label="Nurturing día 3 (horas)" k="nurturing_day_3_hours" delays={delays} setDelays={setDelays} />
          <DelayInput label="Nurturing día 7 (horas)" k="nurturing_day_7_hours" delays={delays} setDelays={setDelays} />
          <DelayInput label="Follow-up post cotización (horas)" k="post_quote_followup_hours" delays={delays} setDelays={setDelays} />
        </div>
        <button onClick={saveDelays} disabled={user?.role !== "admin"}
                className="btn-accent mt-6 disabled:opacity-40" data-testid="save-delays">
          Guardar tiempos
        </button>
        <p className="mt-3 text-[11px] text-[#6B7280]">Los cambios se aplican a nuevos leads. Los programados existentes mantienen su fecha.</p>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="py-2 border-b border-[#EAECEA] last:border-b-0">
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">{label}</p>
      <p className="text-[#374151] text-sm mt-0.5 break-all">{children}</p>
    </div>
  );
}

function DelayInput({ label, k, delays, setDelays }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B7280]">{label}</label>
      <input type="number" min={0} value={delays[k] ?? ""}
             onChange={(e) => setDelays({ ...delays, [k]: Number(e.target.value) })}
             className="mt-1 w-full bg-white border border-[#DDE2D5] px-3 py-3 text-[#1F2937] text-sm font-mono focus:outline-none focus:border-accent"
             data-testid={`delay-${k}`} />
    </div>
  );
}
